import he from 'he';
import fp from 'lodash/fp';
import React from 'react';
import { connect } from "react-redux";
import { compose, lifecycle, withHandlers, withStateHandlers } from 'recompose';
import { bindActionCreators } from "redux";
import { validators } from '../../../../services';
import { businessActions, businessSelector } from '../../../../state/modules/business';
import { Icon, Wizard } from '../../../components';
import { Review, ReviewFooter } from './review';
import { SetTeam, SetTeamFooter } from './set-team';

const wizardConfiguration = ({
  ...props
}) => ({
  name: 'team-details-wizard',
  currentStep: props.currentStep,
  steps: [
    {
      name: 'Set team',
      title: <h1>
        <Icon icon='team-large' />
        {props.teamEditing ? ` Update ${fp.get('businessTeam.name')(props.teamEditing)}` : ` Create new team`}
      </h1>,
      component: <SetTeam {...props} />,
      footer: <SetTeamFooter {...props} />
    },
    {
      name: 'Review',
      title: <h1>
        <Icon icon='team-large' />
        {' Review team'}
      </h1>,
      component: <Review {...props} />,
      footer: <ReviewFooter {...props} />
    }
  ]
})

const resetWindowScroll = () => window.scrollTo(0, 0)

const TeamDetails = (props) => (
  <div id='team-details'>
    <Wizard config={wizardConfiguration(props)} />
  </div>
);

const getDataSchema = (team) => ({
  validation: undefined,
  name: {
    value: team ? he.decode(team.businessTeam.name) : undefined,
    validation: undefined,
    validator: validators.notEmpty("Please enter a title for your team.")
  },
  members: {
    value: team ? fp.map(
      fp.get('user.id')
    )(team.members) : [],
    validation: undefined,
    validator: validators.notEmpty("Please add a member to this team.")
  },
  managers: {
    value: team ? fp.flow(
      fp.filter(fp.get('isTeamManager')),
      fp.map(fp.get('user.id'))
    )(team.members) : [],
    validation: undefined,
    validator: undefined
  },
  nonMemberManagers: {
    value: team ? fp.map(
      fp.get('id')
    )(team.nonMemberManagers) :[],
    validation: undefined,
    validator: undefined,
  }
})

const mapStateToProps = (state) => ({
  business: businessSelector(state)
})

const mapDispatchToProps = (dispatch) => ({
  getTeamMembers: bindActionCreators(businessActions.business.getTeamMembers, dispatch),
})

export default compose(
  connect(mapStateToProps, mapDispatchToProps),
  withStateHandlers(
    ({ team }) => ({
      teamEditing: team,
      team: getDataSchema(team),
      currentStep: 0,
      maxStep: 1
    }),
    {
      updateTeam: ({ team }) => (fieldName, value) => ({
        team: {
          ...team,
          [fieldName]: value
        }
      }),
      setTeam: () => (team) => ({
        team
      }),
      stepForward: ({ currentStep, maxStep }) => () => ({
        currentStep: currentStep + 1,
        maxStep: fp.max([currentStep + 1, maxStep])
      }),
      stepBack: ({ currentStep }) => () => ({
        currentStep: currentStep - 1
      }),
      stepTo: () => (step) => ({
        currentStep: step
      })
    }
  ),
  withHandlers({
    stepForward: ({ stepForward }) => () => {
      resetWindowScroll()
      stepForward()
    },
    stepBack: ({ stepBack }) => () => {
      resetWindowScroll()
      stepBack()
    },
    stepTo: ({ stepTo }) => (step) => {
      resetWindowScroll()
      stepTo(step)
    },
    getValidation: ({ team }) => () => {
      const {
        validation,
        name,
        members,
        managers,
        nonMemberManagers
      } = team
      return {
        validation,
        name: {
          ...name,
          validation: name.validator(name.value)
        },
        members: {
          ...members,
          validation: members.validator(members.value)
        },
        managers,
        nonMemberManagers
      }
    }
  }),
  withHandlers({
    onTeamEdited: ({ team, updateTeam }) => (fieldName, value) => {
      const { validator } = team[fieldName]
      const validation = validator ? validator(value) : undefined
      if (validation) {
        updateTeam(fieldName, {
          ...team[fieldName],
          value,
          validation: validation
        })
      } else {
        updateTeam(fieldName, {
          ...team[fieldName],
          value,
          validation: undefined
        })
      }
    },
    onTeamSubmitted: ({ team, setTeam, getValidation, stepForward }) => () => {
      const validated = getValidation()
      const isValid = !(validated.name.validation || validated.members.validation)
      if (isValid) {
        stepForward()
      } else {
        setTeam(validated)
      }
    }
  }),
  withHandlers(
    {
      toggleTeamMember: ({ onTeamEdited, team }) => (teamMemberId) => {
        const nextMembers = fp.contains(teamMemberId)(team.members.value) ?
          fp.filter(
            (userId) => userId !== teamMemberId
          )(team.members.value) :
          team.members.value.concat(teamMemberId)
        onTeamEdited('members', nextMembers)

        if (!fp.contains(teamMemberId)(team.members.value) && 
        fp.contains(teamMemberId)(team.nonMemberManagers.value)) {
          const nextNonMemberManagers = fp.filter(
            (userId) => userId !== teamMemberId
          )(team.nonMemberManagers.value)
          onTeamEdited('nonMemberManagers', nextNonMemberManagers)

          const nextManagers = team.managers.value.concat(teamMemberId)
          onTeamEdited('managers', nextManagers)
        }

        if (fp.contains(teamMemberId)(team.members.value) && 
        fp.contains(teamMemberId)(team.managers.value)) {
          const nextManagers = fp.filter(
            (userId) => userId !== teamMemberId
          )(team.managers.value)
          onTeamEdited('managers', nextManagers)

          const nextNonMemberManagers = team.nonMemberManagers.value.concat(teamMemberId)
          onTeamEdited('nonMemberManagers', nextNonMemberManagers)
        }
      },
      toggleTeamManager: ({ onTeamEdited, team }) => (teamMemberId) => {
        const nextManagers = fp.contains(teamMemberId)(team.managers.value) ?
          fp.filter(
            (userId) => userId !== teamMemberId
          )(team.managers.value) :
          team.managers.value.concat(teamMemberId)
        onTeamEdited('managers', nextManagers)
      },
      toggleNonMemberManager: ({ onTeamEdited, team }) => (teamMemberId) => {
        const nextNonMemberManagers = fp.contains(teamMemberId)(team.nonMemberManagers.value) ?
          fp.filter(
            (userId) => userId !== teamMemberId
          )(team.nonMemberManagers.value) :
          team.nonMemberManagers.value.concat(teamMemberId)
        onTeamEdited('nonMemberManagers', nextNonMemberManagers)
      },
    }
  ),
  lifecycle({
    componentDidMount() {
      const {
        getTeamMembers,
        business
      } = this.props;

      getTeamMembers(business.id)
    },
  })
)(TeamDetails);


// WEBPACK FOOTER //
// ./src/containers/pages/app/teams/team-details.js